import { useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { Outlet, useLocation } from "react-router";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { Trans, useTranslation } from "react-i18next";
import CheckIcon from "../components/CheckIcon";
import RemoveIcon from "../components/RemoveIcon";
import { useJoyride } from "../context/JoyrideContext";

const JoyrideWrapper = () => {
	const { t, i18n } = useTranslation();

	const stepsMap = useMemo(() => {
		return {
			"/": [
				{
					target: "body",
					content: (
						<Box>
							<Typography component="p" variant="h4" gutterBottom>
								{t("welcome")}
							</Typography>
							<Typography>
								<Trans i18nKey="joyride_intro" components={{ b: <b /> }} />
							</Typography>
						</Box>
					),
					disableBeacon: true,
					placement: "center",
				},
				{
					target: "#side-menu-api",
					content: (
						<Box sx={{ textAlign: "left", width: "500px" }}>
							<Typography component="p" gutterBottom sx={{ fontWeight: 600 }}>
								{t("step_n_of_m", { step: 1, total: 7 })}
							</Typography>
							<Typography component="p" gutterBottom>
								{t("joyride_step_1_1")}
							</Typography>
							<Typography component="p">{t("joyride_step_1_2")}</Typography>
						</Box>
					),
					disableBeacon: true,
					placement: "auto",
				},
				{
					target: "#side-menu-recipients",
					content: (
						<Box sx={{ textAlign: "left", width: "500px" }}>
							<Typography component="p" gutterBottom sx={{ fontWeight: 600 }}>
								{t("step_n_of_m", { step: 2, total: 7 })}
							</Typography>
							<Typography component="p" gutterBottom>
								{t("joyride_step_2_1")}
							</Typography>
							<Typography component="p" gutterBottom>
								{t("joyride_step_2_2")}
							</Typography>
							<Typography component="p">{t("joyride_step_2_3")}</Typography>
						</Box>
					),
					disableBeacon: true,
					placement: "auto",
				},
				{
					target: "#add-cargo-button",
					content: (
						<Box sx={{ textAlign: "left", width: "500px" }}>
							<Typography component="p" gutterBottom sx={{ fontWeight: 600 }}>
								{t("step_n_of_m", { step: 3, total: 7 })}
							</Typography>
							<Typography component="p">{t("joyride_step_3_1")}</Typography>
						</Box>
					),
					disableBeacon: true,
					placement: "auto",
				},
				{
					target: ".MuiTableCell-head:nth-of-type(17)",
					content: (
						<Box sx={{ textAlign: "left", width: "500px" }}>
							<Typography component="p" gutterBottom sx={{ fontWeight: 600 }}>
								{t("step_n_of_m", { step: 4, total: 7 })}
							</Typography>
							<Typography component="p" gutterBottom>
								{t("joyride_step_4_1")}
							</Typography>
							<Typography component="p">{t("joyride_step_4_2")}</Typography>
						</Box>
					),
					disableBeacon: true,
					placement: "auto",
				},
				{
					target: "#cargos-view-switcher",
					content: (
						<Box sx={{ textAlign: "left", width: "500px" }}>
							<Typography component="p" gutterBottom sx={{ fontWeight: 600 }}>
								{t("step_n_of_m", { step: 5, total: 7 })}
							</Typography>
							<Typography component="p" gutterBottom>
								{t("joyride_step_5_1")}
							</Typography>
							<Typography component="p">{t("joyride_step_5_2")}</Typography>
						</Box>
					),
					disableBeacon: true,
					placement: "auto",
				},
				{
					target: "body",
					content: (
						<Box sx={{ textAlign: "left", width: "500px" }}>
							<Typography component="p" gutterBottom sx={{ fontWeight: 600 }}>
								{t("step_n_of_m", { step: 6, total: 7 })}
							</Typography>
							<Typography component="p" gutterBottom>
								{t("joyride_step_6_1")}
							</Typography>
							<Typography>{t("joyride_step_6_2")}</Typography>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: "4px",
									mt: "12px",
								}}
							>
								<RemoveIcon onClick={() => null} sx={{ cursor: "default" }} />
								<Typography>&mdash; {t("joyride_step_6_3")}</Typography>
							</Box>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: "4px",
									mt: "6px",
								}}
							>
								<CheckIcon onClick={() => null} sx={{ cursor: "default" }} />
								<Typography>&mdash; {t("joyride_step_6_4")}</Typography>
							</Box>
						</Box>
					),
					disableBeacon: true,
					placement: "center",
				},
				{
					target: "#side-menu-user-stats",
					content: (
						<Box sx={{ textAlign: "left", width: "500px" }}>
							<Typography component="p" gutterBottom sx={{ fontWeight: 600 }}>
								{t("step_n_of_m", { step: 7, total: 7 })}
							</Typography>
							<Typography component="p" gutterBottom>
								{t("joyride_step_7_1")}
							</Typography>
							<Typography component="p">{t("joyride_step_7_2")}</Typography>
						</Box>
					),
					disableBeacon: true,
					placement: "auto",
				},
			],
			"/integrations": [
				// Define your integrations route steps here
			],
			"/user-data": [
				// Define your user-data route steps here
			],
			"/recipients": [
				// Define your recipients route steps here
			],
			"/stats": [
				// Define your stats route steps here
			],
			"/company-stats": [
				// Define your company-stats route steps here
			],
			"/company-users": [
				// Define your company-users route steps here
			],
		};
	}, [i18n.language]);

	const theme = useTheme();
	const { run, startJoyride, stopJoyride } = useJoyride();
	const location = useLocation();
	// const [run, setRun] = useState(false);
	const [steps, setSteps] = useState(stepsMap["/"]);
	const [stepIndex, setStepIndex] = useState(0);

	const defaultOptions = {
		arrowColor: theme.palette.primary.main,
		backgroundColor: "#fff",
		beaconSize: 36,
		overlayColor: "rgba(0, 0, 0, 0.5)",
		primaryColor: theme.palette.primary.main,
		spotlightShadow: "0 0 15px rgba(0, 0, 0, 0.5)",
		textColor: "#333",
		width: "auto",
		zIndex: 1500,
	};

	useEffect(() => {
		if (localStorage.joyride !== "1") {
			startJoyride();
			localStorage.joyride = "1";
		}
	}, [startJoyride]);

	const callback = (data) => {
		const { action, index, status, type } = data;
		if (
			[STATUS.FINISHED, STATUS.SKIPPED, STATUS.CLOSE].includes(status) ||
			action === "close"
		) {
			stopJoyride();
			setStepIndex(0);
		} else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
			const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

			setStepIndex(nextStepIndex);
		}
	};

	useEffect(() => {
		if (stepsMap[location.pathname]) {
			setSteps(stepsMap[location.pathname]);
			setStepIndex(0);
		}
	}, [location]);

	return (
		<>
			<Outlet />
			<Joyride
				callback={callback}
				steps={steps}
				run={run}
				stepIndex={stepIndex}
				continuous
				// hideCloseButton
				scrollToFirstStep
				disableOverlayClose
				// disableCloseOnEsc
				locale={{
					back: t("back"),
					close: t("close"),
					last: t("finish"),
					next: t("next"),
					open: t("open_window"),
					skip: t("skip_tutorial"),
				}}
				styles={{
					options: defaultOptions,
					tooltipContent: {
						padding: "10px 10px 0 10px",
					},
					spotlight: {
						border: `2px solid ${theme.palette.primary.main}`,
					},
					tooltip: {
						border: `2px solid ${theme.palette.primary.main}`,
					},
					buttonBack: {
						marginLeft: 0,
						marginRight: 5,
					},
				}}
			/>
		</>
	);
};

export default JoyrideWrapper;
